/**
 * IDMS Assessment Executor
 *
 * Handles the 'idms-assessment' and 'pipeline-idms-assessment' node execution.
 * Calls the ModernizeIT API Layer /idmsassessment endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream idms_analysis job is 'completed' (poll if needed)
 * 2. POST /idmsassessment -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for upstream IDMS Analysis to complete before starting.
 * Optionally passes idms_compatibility_job_id for richer assessment.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the IDMS assessment node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing IDMS assessment...')

  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream IDMS Analysis job exists
  const analysisJobId = context.jobs?.idms_analysis_job_id
  if (!analysisJobId) {
    throw new Error('IDMS Assessment requires IDMS Analysis job ID. Run IDMS Analysis first.')
  }

  // 2. Verify upstream job is completed
  console.log(`[IdmsAssessmentExecutor] Verifying upstream analysis job ${analysisJobId}...`)
  onProgress(2, 'Waiting for IDMS Analysis to complete...')

  try {
    await pollJobUntilComplete(analysisJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for IDMS Analysis: ${message}`)
      },
      signal
    })
    console.log(`[IdmsAssessmentExecutor] Upstream analysis job ${analysisJobId} completed`)
  } catch (error) {
    throw new Error(`Upstream IDMS Analysis job failed: ${error.message}`)
  }

  onProgress(8, 'IDMS Analysis complete, starting assessment...')

  // 3. Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    idms_analysis_job_id: analysisJobId,
  }

  // Optionally include compatibility job for richer assessment
  const compatJobId = context.jobs?.idms_compatibility_job_id
  if (compatJobId) {
    requestBody.idms_compatibility_job_id = compatJobId
    console.log(`[IdmsAssessmentExecutor] Including compatibility job ${compatJobId}`)
  }

  onProgress(10, 'Submitting assessment job...')

  // 4. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/idmsassessment`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'IDMS assessment failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[IdmsAssessmentExecutor] Job created: ${jobId}, polling...`)
  onProgress(15, `Job started: ${jobId}`)

  // 5. Poll for completion
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[IdmsAssessmentExecutor] Job completed')
  onProgress(95, 'Extracting results...')

  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'IDMS assessment complete')

  return formatResult(jobId, outputData, context.jobs)
}

function formatResult(jobId, data, contextJobs = {}) {
  const resultJobs = {
    ...contextJobs,
    idms_assessment_job_id: jobId,
  }

  return {
    jobs: resultJobs,
    success: data.success !== false,
    status: data.status || 'completed',
    summary: data.summary || {},
    graph: data.graph || null,
    duration_ms: data.duration_ms,
    error: data.error || null,
  }
}

export default {
  execute,
}
